import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AccountUser } from '../../../../models/account-user.model';
import { concatMap, tap } from 'rxjs/operators';
import { LexbeCase } from '../../../../models/case.model';
import { AccountAdminService } from '../../../../services/account-admin.service';
import { LexbeCodingField } from '../../../../models/lexbe-coding-field.model';
import { AccountUserTypeGuid, AccountUserTypes } from '../../../../models/account-user-type';
import { MatSelectionListChange } from '@angular/material/list';
import { NotificationManager } from '../../../../managers/notification.manager';

@Component({
    selector: 'lex-manage-user-edit',
    templateUrl: './manage-user-edit.component.html',
    styleUrls: ['./manage-user-edit.component.scss'],
    standalone: false
})
export class ManageUserEditComponent implements OnChanges {

	@Input() accountId: string;
	@Input() user: AccountUser;
	@Output() clearSelectionChange = new EventEmitter();

	caseIds: string[] = [];
	customFields: LexbeCodingField[] = [];
	selectedCustomFields: LexbeCodingField[] = [];
	userType: AccountUserTypeGuid;
	twoFactorEnabled: boolean = false;
	cases: LexbeCase[] = [];
	accountUserTypes = new AccountUserTypes();

	constructor(private accountAdminService: AccountAdminService, private notificationManager: NotificationManager) { }

	ngOnChanges(changes: SimpleChanges): void {

		if (changes.user && this.user) {

			this.userType = this.user.userType;
			this.caseIds = this.user.caseIds;
			this.twoFactorEnabled = this.user.twoFactorEnabled;

			this.generateSelectionList();
		}
	}

	generateSelectionList(): void {
		if (this.userType === AccountUserTypeGuid.LimitedUser) {
			this.accountAdminService.getCustomFields(this.accountId).pipe(
				tap(data => {
					this.customFields = data;
				}),
				concatMap(() => this.accountAdminService.getSelectedCustomFields(this.accountId, this.user.userId))
			).subscribe(data => {
				this.selectedCustomFields = data;
			});
		}
		else {
			this.accountAdminService.getCases(this.accountId).subscribe(data => {
				this.cases = data;
			});
		}
	}

	selectUserTypeChange(): void {
		this.generateSelectionList();
	}

	selectAllAccess(): void {
		this.caseIds = this.cases.map(x => x.caseId);
	}

	removeAllAccess(): void {
		this.caseIds = [];
	}

	selectAllFields(): void {
		const sharedAllDocId = '11111111-1111-1111-1111-111111111111';
		this.selectedCustomFields = this.customFields.filter(x => x.fieldId === sharedAllDocId);
	}

	removeAllFields(): void {
		this.selectedCustomFields = [];
	}

	updateAccountUser(): void {
		this.user.userType = this.userType;
		this.user.twoFactorEnabled = this.twoFactorEnabled;

		if (this.userType === AccountUserTypeGuid.LimitedUser) {
			this.user.customFieldIds = this.selectedCustomFields.map(x => x.caseId + ';' + x.fieldId);
			this.user.caseIds = this.selectedCustomFields.map(x => x.caseId);
		}
		else {
			this.user.caseIds = this.caseIds;
			this.user.customFieldIds = [];
		}

		this.accountAdminService.updateAccountUser(this.accountId, this.user).subscribe(() => {
			this.clearSelectionChange.emit(true);
		}, err => {
			this.clearSelectionChange.emit(false);
			let message = `Update user encountered error`;
			if (err?.error)
				message += `: ${err.error}`;
			else if (err?.status === 403)
				message += `: Can not change two factor authentication. Email address must be confirmed.`;

			this.notificationManager.displaySnackBarMessage({
				message: message,
				type: 'error',
				ttl: 7500
			})
		});
	}

	selectCustomFieldChange(event: MatSelectionListChange): void {
		event.options.forEach((option) => {
			if (option.selected) {
				const newField = option.value;
				this.selectedCustomFields = this.selectedCustomFields.filter(x => (x.caseId !== newField.caseId) || (x.caseId === newField.caseId && x.fieldId === newField.fieldId));
			}
		})
	}

	cancel(): void {
		this.clearSelectionChange.emit(false);
	}

	compareCustomFields(obj1: LexbeCodingField, obj2: LexbeCodingField): boolean {
		return obj1.caseId === obj2.caseId && obj1.fieldId === obj2.fieldId;
	}
}
